import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {PostPreview} from "../components/PostPreview";
import fetchPFP from '../functions/fetchPFP';
import formatTimeDifference from '../functions/formatTimeDifference';
import { Link } from 'react-router-dom';
import fetchUsernameWithUID from '../functions/fetchUsernameWithUID';

const Activity = () => {
    const [activities, setActivities] = useState([]);

    useEffect(() => {
        const fetchActivities = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    // Handle not authenticated
                    return;
                }

                const activitiesRef = collection(db, 'users', currentUser.uid, 'activities');
                const activitiesQuery = query(activitiesRef, orderBy('timestamp', 'desc'));
                const querySnapshot = await getDocs(activitiesQuery);

                const activitiesData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setActivities(activitiesData);
            } catch (error) {
                console.error('Error fetching activities:', error);
            }
        };

        fetchActivities();
    }, []);

    const activityComponents = {
        like: ({ activity }) => (
            <ActivityComponent key={activity.id} activity={activity} action="liked" />
        ),
        repost: ({ activity }) => (
            <ActivityComponent key={activity.id} activity={activity} action="reposted" />
        ),
        comment: ({ activity }) => (
            <ActivityComponent key={activity.id} activity={activity} action="commented on" />
        ),
        add_rating: ({ activity }) => (
            <RatingActivity key={activity.id} activity={activity} />
        ),
        create_new_list: ({ activity }) => (
            <ActivityComponent key={activity.id} activity={activity} action="created a new list" />
        ),
    };

    return (
        <div className="p-2 pt-20">
            {activities.map((activity) => {
                const ActivityComponent = activityComponents[activity.activity];
                if (!ActivityComponent) {
                    return null;
                }
                return <ActivityComponent activity={activity} />;
            })}
        </div>
    );
};
const ActivityComponent = ({ activity, action }) => {
    const [postAuthor, setPostAuthor] = useState(null);
    const [profilePicture, setProfilePicture] = useState(null);
    const [post, setPost] = useState(null);

    const fetchPost = async (postId) => {
        try {
            const postRef = doc(db, 'posts', postId);
            const postDoc = await getDoc(postRef);
            return postDoc.data();
        } catch (error) {
            console.error('Error fetching post:', error);
            return null;
        }
    };
    
    const fetchPostAuthor = async (authorId) => {
        try {
            const authorRef = doc(db, 'users', authorId);
            const authorDoc = await getDoc(authorRef);
            const authorData = authorDoc.data();
            if (!authorData) {
                console.error('Post author not found');
                return null;
            }
    
            const profilePicture = await fetchPFP(authorData.uid);
            setProfilePicture(profilePicture);
    
            return authorData;
        } catch (error) {
            console.error('Error fetching post author:', error);
            return null;
        }
    };
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                console.log('Fetching data for activity:', activity);
                const post = await fetchPost(activity.interacted_with);
                console.log('Fetched post:', post);
                if (!post) {
                    console.error('Post not found');
                    return;
                }
    
                setPost(post);
    
                const postAuthor = await fetchPostAuthor(post.uid);
                console.log('Fetched post author:', postAuthor);
                if (!postAuthor) {
                    console.error('Post author not found');
                    return;
                }
                setPostAuthor(postAuthor);
    
                if (activity.activity === 'like') {
                    console.log('Fetching display name for post author');
                    const displayName = await fetchUsernameWithUID(post.uid);
                    console.log('Fetched display name:', displayName);
                    setPostAuthor({ displayName });
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
    
        console.log('Fetching data for activity:', activity);
        fetchData();
    }, [activity.interacted_with, activity.activity]);
    

    const defaultProfilePicture = require('../assets/default-profile-picture.jpg');

    return (
        <div className='ml-24 z-0'>
            <section className="flex flex-col sm:flex-col  w-full gap-2 my-2 rounded-md">
                <section className="w-full flex flex-col sm:flex-row text-sm items-center">
                    <p className="mr-1">You {action}</p>
                    <Link to={`/users/${postAuthor?.username}`} className="font-semibold flex flex-row items-center">
                        {profilePicture ? (
                            <img src={profilePicture} alt="Profile Picture" className="h-5 w-5 rounded-full border-4 border-white" />
                        ) : (
                            <img src={defaultProfilePicture} alt="Profile Picture" className="h-5 w-5 rounded-full border-4 border-white" />
                        )}
                        
                        {postAuthor && <p className="font-normal"><span className="text-blue-500">@</span><span>{postAuthor?.displayName}</span>'s post</p>}
                    </Link>
                </section>
                <h3 className='text-xs'>{formatTimeDifference(activity.timestamp ? new Date(activity.timestamp.toMillis()) : '')}</h3>
            </section>
            <div>
                <PostPreview post={activity.interacted_with} />
            </div>
        </div>
    );
};

const RatingActivity = ({ activity }) => {
    const [ratedBook, setRatedBook] = useState(null);

    useEffect(() => {
        const fetchRatedBook = async () => {
            try {
                const ratedBookRef = doc(db, 'books', activity.interacted_with);
                const ratedBookDoc = await getDoc(ratedBookRef);
                const ratedBookData = ratedBookDoc.data();

                if (!ratedBookData) {
                    console.error('Rated book not found');
                    return;
                }

                setRatedBook(ratedBookData);
            } catch (error) {
                console.error('Error fetching rated book:', error);
            }
        };

        fetchRatedBook();
    }, [activity.interacted_with]);

    if (!ratedBook) {
        return <div>Loading...</div>;
    }

    console.log('Rated book:', ratedBook);

    console.log("identifier", ratedBook.volumeInfo.industryIdentifiers[0]  );

    console.log("activity.timestamp", activity.timestamp   );

    return (
        <div>
            <section className="flex flex-col sm:flex-row w-full z-0">
                <section className="w-full flex flex-row text-sm items-center">
                    <p className="mr-1">You added a rating to</p>
                    <Link to={`/bookinfo/${ratedBook.volumeInfo.industryIdentifiers[0].identifier}`} className="font-semibold flex flex-row items-center">
                        <span>{ratedBook.volumeInfo.title}</span>
                    </Link>
                </section>
                <h3 className='text-xs'>{formatTimeDifference(activity.timestamp ? new Date(activity.timestamp.toMillis()) : '')}</h3>
            </section>
        </div>
    );
};

export default Activity;
